// ADR-012 i18n — "agents" cluster label catalog. Spread into the central LABELS
// in labels.ts. EN values MUST equal the screen's current hardcoded English so
// existing text-based tests stay green; HE values are the Hebrew translation.
export const AGENTS_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  Settings container (SettingsPage)
  // ═══════════════════════════════════════════════════════════════════════
  settingsTitle: { en: 'Settings', he: 'הגדרות' },
  settingsDescription: {
    en: 'Per-tenant administration — profile, chat-widget appearance, users, and CRM integration.',
    he: 'ניהול לכל ארגון — פרופיל, מראה רכיב הצ׳אט, משתמשים ואינטגרציית CRM.',
  },
  settingsSectionsAria: { en: 'Settings sections', he: 'מקטעי הגדרות' },
  settingsNoTenant: { en: 'No tenant selected.', he: 'לא נבחר ארגון.' },
  settingsTabProfile: { en: 'Profile', he: 'פרופיל' },
  settingsTabAppearance: { en: 'Chat Appearance', he: 'מראה הצ׳אט' },
  settingsTabUsers: { en: 'Users', he: 'משתמשים' },
  settingsTabCrm: { en: 'CRM', he: 'CRM' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Chat Appearance editor (ChatAppearanceEditor)
  // ═══════════════════════════════════════════════════════════════════════
  chatAppearanceLoading: { en: 'Loading chat appearance...', he: 'טוען את מראה הצ׳אט...' },
  chatAppearanceHeading: { en: 'Chat Appearance', he: 'מראה הצ׳אט' },
  chatAppearancePreviewHeading: { en: 'Live preview', he: 'תצוגה מקדימה חיה' },
  chatAppearancePreviewUnavailable: {
    en: 'The preview cannot load because the widget address is not available right now.',
    he: 'לא ניתן לטעון את התצוגה המקדימה כי כתובת הווידג׳ט אינה זמינה כרגע.',
  },
  chatAppearancePreviewNeedsPermission: {
    en: 'The live preview needs the Bots management permission.',
    he: 'התצוגה המקדימה החיה דורשת הרשאת ניהול בוטים.',
  },
  chatAppearanceHelp: {
    en: 'Controls the look of your embedded chat widget (Constellation RTL). Change the brand colour and every other element below; customers see it live on the widget.',
    he: 'שולט במראה של רכיב הצ׳אט המוטמע (Constellation RTL). שנה את צבע המותג וכל שאר האלמנטים למטה; הלקוחות רואים זאת בזמן אמת ברכיב.',
  },
  chatAppearanceReadonly: {
    en: 'You have read-only access to the chat appearance. Contact an administrator to make changes.',
    he: 'יש לך גישת קריאה בלבד למראה הצ׳אט. פנה למנהל כדי לבצע שינויים.',
  },
  chatAppearanceSavedAt: { en: 'Saved at {time}.', he: 'נשמר בשעה {time}.' },
  chatAppearanceColorLabel: { en: 'Chat colour', he: 'צבע הצ׳אט' },
  chatAppearanceColorHelp: {
    en: 'The brand / accent colour — header, customer bubbles, send button.',
    he: 'צבע המותג / ההדגשה — כותרת, בועות הלקוח, כפתור השליחה.',
  },
  chatAppearanceColorHexAria: { en: 'Chat colour hex', he: 'קוד hex לצבע הצ׳אט' },
  chatAppearanceDarkMode: { en: 'Dark mode', he: 'מצב כהה' },
  chatAppearanceDirection: { en: 'Direction', he: 'כיוון' },
  chatAppearanceDirRtl: { en: 'Right-to-left (Hebrew)', he: 'ימין לשמאל (עברית)' },
  chatAppearanceDirLtr: { en: 'Left-to-right', he: 'שמאל לימין' },
  chatAppearanceLanguageTag: { en: 'Language tag', he: 'תגית שפה' },
  chatAppearanceHeaderTitle: { en: 'Header title', he: 'כותרת עליונה' },
  chatAppearanceHeaderSubtitle: { en: 'Header subtitle', he: 'כותרת משנה' },
  chatAppearanceSubtitleHelp: {
    en: 'Leave empty to show a live status line.',
    he: 'השאר ריק כדי להציג שורת סטטוס חיה.',
  },
  chatAppearanceGreeting: { en: 'Greeting', he: 'ברכת פתיחה' },
  chatAppearanceLogoInitials: { en: 'Logo initials', he: 'ראשי תיבות ללוגו' },
  chatAppearanceLauncherSize: { en: 'Launcher size', he: 'גודל כפתור הפתיחה' },
  chatAppearanceLauncherSizeHelp: {
    en: 'Diameter of the closed-state launcher button, in pixels.',
    he: 'קוטר כפתור הפתיחה במצב סגור, בפיקסלים.',
  },
  chatAppearanceLauncherRadius: { en: 'Launcher roundness', he: 'עיגול כפתור הפתיחה' },
  chatAppearanceLauncherRadiusHelp: {
    en: '0 = square badge, 50 = perfect circle.',
    he: '0 = תג מרובע, 50 = עיגול מושלם.',
  },
  chatAppearanceWindowSize: { en: 'Chat window size', he: 'גודל חלון הצ׳אט' },
  chatAppearanceWindowSizeHelp: {
    en: 'Scales with the visitor’s screen size. Full docks the chat as a full-height sidebar.',
    he: 'משתנה לפי גודל המסך של המבקר. "מלא" מציג את הצ’אט כפאנל צדדי בגובה מלא.',
  },
  chatAppearanceWindowSizeSmall: { en: 'Small', he: 'קטן' },
  chatAppearanceWindowSizeMedium: { en: 'Medium', he: 'בינוני' },
  chatAppearanceWindowSizeBig: { en: 'Big', he: 'גדול' },
  chatAppearanceWindowSizeFull: { en: 'Full', he: 'מלא' },
  chatAppearanceMainColorLabel: { en: 'Main color', he: 'צבע ראשי' },
  chatAppearanceLauncherColor: { en: 'Launcher color', he: 'צבע כפתור הפתיחה' },
  chatAppearanceLauncherColorHelp: {
    en: 'Background of the closed chat button. Default: a gradient of the main color.',
    he: 'צבע הרקע של כפתור הצ׳אט הסגור. ברירת מחדל: מעבר גוון של הצבע הראשי.',
  },
  chatAppearanceLauncherIconColor: { en: 'Launcher icon color', he: 'צבע הסמל בכפתור' },
  chatAppearanceLauncherIconColorHelp: {
    en: 'Color of the symbol inside the launcher. Default: chosen automatically for contrast.',
    he: 'צבע הסמל שבתוך כפתור הפתיחה. ברירת מחדל: נבחר אוטומטית לפי ניגודיות.',
  },
  chatAppearanceSecondaryColor: { en: 'Secondary color', he: 'צבע משני' },
  chatAppearanceSecondaryColorHelp: {
    en: 'Assistant message bubbles and the soft background accents.',
    he: 'בועות ההודעות של הבוט והדגשות הרקע הרכות.',
  },
  chatAppearanceColorDefault: { en: 'Use default', he: 'ברירת מחדל' },
  chatAppearanceLauncherIcon: { en: 'Chat symbol', he: 'סמל הצ׳אט' },
  chatAppearanceLauncherIconHelp: {
    en: 'The symbol shown on the closed chat button.',
    he: 'הסמל שמוצג על כפתור הצ׳אט הסגור.',
  },
  chatAppearanceIconName: {
    en: 'Symbol: {name}',
    he: 'סמל: {name}',
  },
  chatAppearanceShadow: { en: 'Shadow behind the chat', he: 'צל מאחורי הצ׳אט' },
  chatAppearanceExpand: { en: 'Show expand button', he: 'הצגת כפתור הגדלה' },
  chatAppearanceScreenshot: { en: 'Show screenshot button', he: 'הצגת כפתור צילום מסך' },
  chatAppearanceScreenshotHelp: {
    en: 'Needs file attachments enabled for your tenant.',
    he: 'דורש שליחת קבצים מופעלת.',
  },
  chatAppearanceDisclaimer: {
    en: 'Show AI disclaimer under the input',
    he: 'הצגת הבהרת בינה מלאכותית מתחת לתיבת הכתיבה',
  },
  chatAppearanceQuickQuestions: { en: 'Quick questions', he: 'שאלות מהירות' },
  chatAppearanceQuickQuestionsHelp: {
    en: 'Tappable questions shown under the greeting before the visitor writes. An empty list shows no quick questions at all. Up to 8, each up to 80 characters; plain text only.',
    he: 'שאלות שאפשר ללחוץ עליהן, מתחת לברכה לפני שהמבקר כותב. רשימה ריקה לא מציגה שאלות מהירות כלל. עד 8, עד 80 תווים כל אחת; טקסט פשוט בלבד.',
  },
  chatAppearanceQuickQuestionsTitle: { en: 'Quick questions heading (optional)', he: 'כותרת לשאלות המהירות (אופציונלי)' },
  chatAppearanceQuickQuestionsTitleHelp: {
    en: 'Leave empty for the default heading in the widget language ("Frequently asked" / "שאלות נפוצות").',
    he: 'השאירו ריק לכותרת ברירת המחדל בשפת הווידג׳ט ("Frequently asked" / "שאלות נפוצות").',
  },
  chatAppearanceQuickQuestionAdd: { en: 'Add a question', he: 'הוספת שאלה' },
  chatAppearanceQuickQuestionRemove: { en: 'Remove', he: 'הסרה' },
  chatAppearanceQuickQuestionUp: { en: 'Move up', he: 'הזזה למעלה' },
  chatAppearanceQuickQuestionDown: { en: 'Move down', he: 'הזזה למטה' },
  chatAppearanceQuickQuestionItem: { en: 'Quick question {n}', he: 'שאלה מהירה {n}' },
  chatAppearanceQuickQuestionsCount: { en: '{n} of 8 questions', he: '{n} מתוך 8 שאלות' },
  chatAppearanceAnnouncement: { en: 'Welcome-page update (optional)', he: 'עדכון בדף הפתיחה (אופציונלי)' },
  chatAppearanceAnnouncementHelp: {
    en: 'A short note shown on the chat\'s welcome page, under the greeting (for example new opening hours). Plain text, up to 300 characters. Leave empty to show nothing.',
    he: 'הודעה קצרה שמוצגת בדף הפתיחה של הצ׳אט, מתחת לברכה (למשל שעות פעילות חדשות). טקסט פשוט, עד 300 תווים. השאירו ריק כדי לא להציג דבר.',
  },
  chatAppearancePosition: { en: 'Chat position on the page', he: 'מיקום הצ׳אט בעמוד' },
  chatAppearancePositionHelp: {
    en: 'Which bottom corner of your website the chat button and window appear in, in every language. Default: right. This setting overrides the position in your embed code.',
    he: 'באיזו פינה תחתונה של האתר יופיעו כפתור הצ׳אט וחלון הצ׳אט, בכל שפה. ברירת מחדל: ימין. הגדרה זו גוברת על המיקום שבקוד ההטמעה.',
  },
  chatAppearancePositionRight: { en: 'Right', he: 'ימין' },
  chatAppearancePositionLeft: { en: 'Left', he: 'שמאל' },
  chatAppearancePositionUnset: {
    en: 'Not chosen yet: the chat sits at the right unless your embed code says "bottom-left". Choose a side and save to set it here.',
    he: 'עדיין לא נבחר: הצ׳אט מופיע בימין, אלא אם קוד ההטמעה שלכם מציין "bottom-left". בחרו צד ושמרו כדי לקבוע אותו כאן.',
  },
  chatAppearanceLogoUrl: { en: 'Logo image URL', he: 'כתובת תמונת לוגו' },
  chatAppearanceLogoUrlHelp: {
    en: 'https only; leave empty to show the initials.',
    he: '‏https בלבד; השאירו ריק להצגת ראשי התיבות.',
  },
  chatAppearanceSaving: { en: 'Saving...', he: 'שומר...' },
  chatAppearanceSave: { en: 'Save Chat Appearance', he: 'שמור מראה צ׳אט' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Tenant profile editor (TenantProfileEditor)
  // ═══════════════════════════════════════════════════════════════════════
  tenantProfileLoading: { en: 'Loading tenant profile...', he: 'טוען את פרופיל הארגון...' },
  tenantProfileHeading: { en: 'Profile', he: 'פרופיל' },
  tenantProfileSavedAt: { en: 'Saved at {time}.', he: 'נשמר בשעה {time}.' },
  tenantProfileReadonlyNote: {
    en: 'Tenant profile and limits are managed by your platform administrator.',
    he: 'פרופיל הארגון והמגבלות מנוהלים על ידי מנהל הפלטפורמה שלך.',
  },
  tenantProfileName: { en: 'Tenant Name', he: 'שם הארגון' },
  tenantProfileContactEmail: { en: 'Contact Email', he: 'אימייל ליצירת קשר' },
  tenantProfilePlanTier: { en: 'Plan Tier', he: 'רמת מנוי' },
  tenantProfilePlanFree: { en: 'Free', he: 'חינם' },
  tenantProfilePlanStarter: { en: 'Starter', he: 'בסיסי' },
  tenantProfilePlanProfessional: { en: 'Professional', he: 'מקצועי' },
  tenantProfilePlanEnterprise: { en: 'Enterprise', he: 'ארגוני' },
  tenantProfileMaxAgents: { en: 'Max Agents', he: 'מקסימום נציגים' },
  tenantProfileMaxChannels: { en: 'Max Channels per Bot', he: 'מקסימום ערוצים לבוט' },
  tenantProfileStatusLabel: { en: 'Status:', he: 'סטטוס:' },
  tenantProfileLicenseLabel: { en: 'User license (max users):', he: 'רישיון משתמשים (מקסימום משתמשים):' },
  tenantProfileLicenseContact: {
    en: ' — contact a platform admin to change.',
    he: ' — פנה למנהל פלטפורמה כדי לשנות.',
  },
  tenantProfileSaving: { en: 'Saving...', he: 'שומר...' },
  tenantProfileSave: { en: 'Save Profile', he: 'שמור פרופיל' },
  tenantProfileLoadFailed: { en: 'Failed to load tenant profile.', he: 'טעינת פרופיל הארגון נכשלה.' },
  tenantProfileUpdateFailed: { en: 'Failed to update tenant profile.', he: 'עדכון פרופיל הארגון נכשל.' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Agent list (AgentListPage)
  // ═══════════════════════════════════════════════════════════════════════
  agentListLoading: { en: 'Loading agents...', he: 'טוען סוכנים...' },
  agentListTitle: { en: 'Agents', he: 'סוכנים' },
  agentListNewAgent: { en: '+ New Agent', he: '+ סוכן חדש' },
  agentListColName: { en: 'Name', he: 'שם' },
  agentListColType: { en: 'Type', he: 'סוג' },
  agentListColDescription: { en: 'Description', he: 'תיאור' },
  agentListColStatus: { en: 'Status', he: 'סטטוס' },
  agentListColVersion: { en: 'Version', he: 'גרסה' },
  agentListColActions: { en: 'Actions', he: 'פעולות' },
  agentListActive: { en: 'Active', he: 'פעיל' },
  agentListInactive: { en: 'Inactive', he: 'לא פעיל' },
  agentListDeleteConfirm: { en: 'Delete this agent configuration?', he: 'למחוק את תצורת הסוכן הזו?' },
  agentListDeleteFailed: { en: 'Failed to delete agent.', he: 'מחיקת הסוכן נכשלה.' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge source detail (KnowledgeSourceDetailPage)
  // ═══════════════════════════════════════════════════════════════════════
  knowledgeSourceLoading: { en: 'Loading source…', he: 'טוען מקור…' },
  knowledgeSourceNotFound: { en: 'Source not found.', he: 'המקור לא נמצא.' },
  knowledgeSourceBack: { en: '← Back', he: '← חזרה' },
  knowledgeSourceTitle: { en: 'Indexed source', he: 'מקור מאונדקס' },
  knowledgeSourceUrlLabel: { en: 'URL', he: 'כתובת URL' },
  knowledgeSourceFileLabel: { en: 'File', he: 'קובץ' },
  knowledgeSourceStatStatus: { en: 'Status', he: 'סטטוס' },
  knowledgeSourceStatChunks: { en: 'Entries', he: 'פריטים' },
  knowledgeSourceStatTotalChars: { en: 'Total characters', he: 'סך תווים' },
  knowledgeSourceStatCreated: { en: 'Created', he: 'נוצר' },
  knowledgeSourceEnabled: { en: '● Enabled', he: '● מופעל' },
  knowledgeSourceDisabled: { en: '○ Disabled', he: '○ מושבת' },
  knowledgeSourceDisabledPrefix: { en: 'This source is ', he: 'מקור זה ' },
  knowledgeSourceDisabledWord: { en: 'disabled', he: 'מושבת' },
  knowledgeSourceDisabledSuffix: {
    en: ' — the bot will not retrieve any of these entries on customer turns. The entries are still stored; toggle back to re-enable.',
    he: ' — הבוט לא יאחזר אף אחד מהפריטים הללו בפניות לקוחות. הפריטים עדיין נשמרים; החלף בחזרה כדי להפעיל מחדש.',
  },
  knowledgeSourceWhatIndexed: { en: 'What was indexed', he: 'מה אונדקס' },
  knowledgeSourceChunkSingular: { en: 'entry', he: 'פריט' },
  knowledgeSourceChunkPlural: { en: 'entries', he: 'פריטים' },
  knowledgeSourceChunkExplain: {
    en: 'Each entry is one slice of the page\'s text. The bot compares each entry against the customer\'s question to find the best match. If the bot says it doesn\'t have the information even though this page is indexed, check the entries below — often a page returns mostly menus or cookie banners and the real content never made it through.',
    he: 'כל פריט הוא פרוסה אחת מטקסט הדף. הבוט משווה כל פריט לשאלת הלקוח כדי למצוא את ההתאמה הטובה ביותר. אם הבוט אומר שאין לו את המידע למרות שדף זה מאונדקס, בדוק את הפריטים למטה — לעיתים קרובות דף מחזיר בעיקר תפריטים או באנרי עוגיות והתוכן האמיתי מעולם לא עבר.',
  },
  knowledgeSourceNoChunks: { en: 'No entries for this source.', he: 'אין פריטים למקור זה.' },
  knowledgeSourceChunkLabel: { en: 'Entry #{n}', he: 'פריט #{n}' },
  knowledgeSourceChars: { en: '{n} chars', he: '{n} תווים' },
  knowledgeSourceSkipped: { en: '· skipped', he: '· דולג' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge editor (components/agents/KnowledgeEditor)
  // ═══════════════════════════════════════════════════════════════════════
  knowledgeEditorTitle: { en: 'Knowledge', he: 'ידע' },
  knowledgeEditorLoadFailed: { en: 'Failed to load knowledge sources.', he: 'טעינת מקורות הידע נכשלה.' },
  knowledgeEditorUploadFailed: { en: 'Upload failed.', he: 'ההעלאה נכשלה.' },
  knowledgeEditorAddUrlFailed: { en: 'Failed to add URL.', he: 'הוספת הכתובת נכשלה.' },
  knowledgeEditorDeleteFailed: { en: 'Failed to delete source.', he: 'מחיקת המקור נכשלה.' },
  knowledgeEditorToggleFailed: { en: 'Failed to toggle source.', he: 'החלפת מצב המקור נכשלה.' },
  knowledgeEditorInvalidUrl: { en: 'Enter a valid http(s) URL.', he: 'הזן כתובת http(s) תקינה.' },
  knowledgeEditorPendingHint: {
    en: 'Files and URLs you add here will be uploaded for indexing right after the agent is created.',
    he: 'קבצים וכתובות שתוסיף כאן יועלו לאינדוקס מיד לאחר יצירת הסוכן.',
  },
  knowledgeEditorFiles: { en: 'Files', he: 'קבצים' },
  knowledgeEditorFilesHint: {
    en: 'Word, PDF, images, presentations, plain text',
    he: 'Word, PDF, תמונות, מצגות, טקסט רגיל',
  },
  knowledgeEditorWebPages: { en: 'Web pages', he: 'דפי אינטרנט' },
  knowledgeEditorWebPagesHint: {
    en: 'Public URLs we fetch and index',
    he: 'כתובות ציבוריות שאנו מאחזרים ומאנדקסים',
  },
  knowledgeEditorAdd: { en: 'Add', he: 'הוסף' },
  knowledgeEditorNoFiles: { en: 'No files yet.', he: 'עדיין אין קבצים.' },
  knowledgeEditorNoUrls: { en: 'No URLs yet.', he: 'עדיין אין כתובות.' },
  knowledgeEditorChunksCount: { en: '{n} entries', he: '{n} פריטים' },
  knowledgeEditorSourceFailedHint: { en: 'This item failed to process.', he: 'פריט זה נכשל בעיבוד.' },
  knowledgeEditorPendingUpload: { en: 'pending upload', he: 'ממתין להעלאה' },
  knowledgeEditorRemoveAria: { en: 'Remove {name}', he: 'הסר {name}' },
  knowledgeEditorEnableAria: { en: 'Enable {name}', he: 'הפעל {name}' },
  knowledgeEditorDisableAria: { en: 'Disable {name}', he: 'השבת {name}' },
  knowledgeEditorEnabled: { en: 'Enabled', he: 'מופעל' },
  knowledgeEditorDisabled: { en: 'Disabled', he: 'מושבת' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Agent form (AgentFormPage + PipelineSliderControl)
  // ═══════════════════════════════════════════════════════════════════════
  agentFormSystemWide: { en: '(system-wide, all AI agents)', he: '(כלל-מערכתי, כל סוכני ה-AI)' },
  agentFormSaved: { en: 'Saved', he: 'נשמר' },
  agentFormSliderSaved: {
    en: 'Saved. This change takes effect within a few seconds.',
    he: 'נשמר. השינוי ייכנס לתוקף תוך מספר שניות.',
  },
  agentFormSliderSaveFailed: {
    en: 'Save failed. Check that you still have admin role.',
    he: 'השמירה נכשלה. בדוק שעדיין יש לך תפקיד מנהל.',
  },
  agentFormConfidenceLabel: { en: 'Response confidence threshold', he: 'סף ביטחון לתשובה' },
  agentFormConfidenceDesc: {
    en: 'Minimum certainty (0-100) the bot must reach to answer the customer directly. Below this, retrieval retries (up to 2×) and then escalates to a human consultation. Default 80%. Lower → more direct answers; higher → escalates sooner.',
    he: 'הוודאות המינימלית (0-100) שהבוט חייב להגיע אליה כדי לענות ללקוח ישירות. מתחת לזה, האחזור מנסה שוב (עד פי 2) ואז מסלים לייעוץ אנושי. ברירת מחדל 80%. נמוך יותר ← יותר תשובות ישירות; גבוה יותר ← מסלים מוקדם יותר.',
  },
  agentFormSimilarityLabel: { en: 'How closely a piece of knowledge must match the question', he: 'עד כמה פיסת ידע צריכה להתאים לשאלה' },
  agentFormSimilarityDesc: {
    en: 'Controls how strict the bot is when deciding a piece of knowledge is relevant enough to use. Lower = looser matching (more results, some less relevant); higher = stricter (fewer, more precise results). If your knowledge is in mixed Hebrew/English, a lower setting usually works better.',
    he: 'קובע עד כמה הבוט מחמיר בבחירת פיסת ידע רלוונטית מספיק לשימוש. נמוך יותר = התאמה רפויה יותר (יותר תוצאות, חלקן פחות רלוונטיות); גבוה יותר = מחמיר יותר (פחות תוצאות, אך מדויקות יותר). אם הידע שלך משלב עברית ואנגלית, ערך נמוך יותר בדרך כלל עובד טוב יותר.',
  },
  agentFormLoading: { en: 'Loading agent...', he: 'טוען סוכן...' },
  agentFormEditTitle: { en: 'Edit Agent', he: 'עריכת סוכן' },
  agentFormNewTitle: { en: 'New Agent', he: 'סוכן חדש' },
  agentFormImportTickets: { en: 'Import historical tickets →', he: 'ייבא כרטיסים היסטוריים →' },
  agentFormName: { en: 'Name', he: 'שם' },
  agentFormAgentType: { en: 'Agent Type', he: 'סוג סוכן' },
  agentFormTypeDummy: { en: 'Dummy (static response)', he: 'דמה (תשובה סטטית)' },
  agentFormTypeAi: { en: 'AI (LLM-powered)', he: 'AI (מבוסס LLM)' },
  agentFormDescription: { en: 'Description', he: 'תיאור' },
  agentFormStaticResponse: { en: 'Static Response', he: 'תשובה סטטית' },
  agentFormStaticResponsePlaceholder: { en: 'I got your message, Thank you!', he: 'קיבלתי את הודעתך, תודה!' },
  agentFormModel: { en: 'Model', he: 'מודל' },
  agentFormSelectModel: { en: 'Select model...', he: 'בחר מודל...' },
  agentFormAgentInstruction: { en: 'Agent Instruction', he: 'הנחיית סוכן' },
  agentFormAgentInstructionHelp: {
    en: 'Identity, role, and behaviour for this agent. Stored as the agent’s Brain alongside Limitations, Methods, and Menus below.',
    he: 'זהות, תפקיד והתנהגות עבור סוכן זה. נשמר כמוח של הסוכן לצד מגבלות, שיטות ותפריטים למטה.',
  },
  agentFormAgentInstructionPlaceholder: {
    en: 'You are a helpful customer support assistant...',
    he: 'אתה עוזר תמיכת לקוחות מסייע...',
  },
  agentFormKnowledgeRules: { en: 'Agent Knowledge & Rules', he: 'ידע וכללי סוכן' },
  agentFormLimitations: { en: 'Limitations', he: 'מגבלות' },
  agentFormLimitationsHelp: {
    en: 'Things this agent must NOT do. One short rule per entry.',
    he: 'דברים שסוכן זה אסור לו לעשות. כלל קצר אחד לכל רשומה.',
  },
  agentFormLimitationsPlaceholder: {
    en: 'e.g. Do not promise refunds without escalation',
    he: 'לדוגמה: אל תבטיח החזרים ללא הסלמה',
  },
  agentFormMethods: { en: 'Work methods', he: 'שיטות עבודה' },
  agentFormMethodsHelp: {
    en: 'How this agent typically handles common scenarios.',
    he: 'כיצד סוכן זה מטפל בדרך כלל בתרחישים נפוצים.',
  },
  agentFormMethodsPlaceholder: {
    en: 'e.g. Always ask for the order number first',
    he: 'לדוגמה: תמיד בקש קודם את מספר ההזמנה',
  },
  agentFormMenus: { en: 'Menus', he: 'תפריטים' },
  agentFormMenusHelp: {
    en: 'Pre-canned options the agent can offer. Useful for scope-restricted bots.',
    he: 'אפשרויות מוכנות מראש שהסוכן יכול להציע. שימושי לבוטים מוגבלי היקף.',
  },
  agentFormMenusPlaceholder: {
    en: 'e.g. Returns / Shipping / Account',
    he: 'לדוגמה: החזרות / משלוחים / חשבון',
  },
  agentFormTemperature: { en: 'Temperature', he: 'טמפרטורה' },
  agentFormMaxTokens: { en: 'Max Tokens', he: 'מקסימום טוקנים' },
  agentFormConnectedBanks: { en: 'Connected Knowledge Banks', he: 'מאגרי ידע מחוברים' },
  agentFormConnectedBanksHelp: {
    en: 'Pick one or more banks the agent retrieves from. Manage banks at',
    he: 'בחר מאגר אחד או יותר שהסוכן מאחזר מהם. נהל מאגרים ב',
  },
  agentFormManageBanksLink: { en: 'Knowledge Banks', he: 'מאגרי ידע' },
  agentFormNoBanks: {
    en: 'No banks defined yet for this tenant. Create one first.',
    he: 'עדיין לא הוגדרו מאגרים לארגון זה. צור אחד תחילה.',
  },
  agentFormBankStats: { en: '{sources} sources · {entries} entries', he: '{sources} מקורות · {entries} רשומות' },
  agentFormBanksSelected: { en: '{n} bank(s) selected.', he: '{n} מאגרים נבחרו.' },
  agentFormSaving: { en: 'Saving...', he: 'שומר...' },
  agentFormUpdate: { en: 'Update Agent', he: 'עדכן סוכן' },
  agentFormCreate: { en: 'Create Agent', he: 'צור סוכן' },
  agentFormBrainSaveFailedPrefix: {
    en: 'Agent created, but the Brain section failed to save. ',
    he: 'הסוכן נוצר, אך שמירת מקטע המוח נכשלה. ',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Agent observability (components/agents/AgentObservabilitySection)
  // ═══════════════════════════════════════════════════════════════════════
  agentObsTitle: { en: 'Activity & Learning', he: 'פעילות ולמידה' },
  agentObsSubtitle: {
    en: 'Read-only views of what the agent has learned (Learned Notes), customer history, and feedback received. All three are machine-curated.',
    he: 'תצוגות לקריאה בלבד של מה שהסוכן למד (הערות שנלמדו), היסטוריית לקוחות ומשוב שהתקבל. שלושתן נאספות אוטומטית.',
  },
  agentObsRunLearningAria: { en: 'Update learning now for this agent', he: 'עדכן למידה כעת עבור סוכן זה' },
  agentObsRunning: { en: 'Running…', he: 'פועל…' },
  agentObsRunLearning: { en: 'Update learning now', he: 'עדכן למידה כעת' },
  agentObsRunLearningHelp: {
    en: 'Runs the learning batch on demand instead of waiting for the nightly run. New behavior surfaces below as suggestions to review; facts go to the Learning Bank.',
    he: 'מריץ את מקבץ הלמידה לפי דרישה במקום להמתין להרצה הלילית. התנהגות חדשה מופיעה למטה כהצעות לבדיקה; עובדות עוברות למאגר הלמידה.',
  },
  agentObsLearningSummary: {
    en: 'Processed {processed} · {proposals} proposals · {facts} to Learning Bank · {dropped} dropped',
    he: 'עובדו {processed} · {proposals} הצעות · {facts} למאגר הלמידה · {dropped} נדחו',
  },
  agentObsLearningLeft: { en: ' · {left} left', he: ' · {left} נותרו' },
  agentObsRunLearningFailed: { en: 'Failed to run learning.', he: 'הרצת הלמידה נכשלה.' },
  agentObsTabCore: { en: 'Learned Notes', he: 'הערות שנלמדו' },
  agentObsTabMemory: { en: 'Customer history', he: 'היסטוריית לקוחות' },
  agentObsTabFeedback: { en: 'Feedback', he: 'משוב' },
  agentObsLoadFailed: { en: 'Failed to load {tab}.', he: 'טעינת {tab} נכשלה.' },
  agentObsRefreshCoreFailed: { en: 'Failed to refresh core.', he: 'רענון הליבה נכשל.' },
  agentObsCoreEmpty: {
    en: 'No machine-curated notes yet. They appear after the nightly batch promotes high-priority Memory entries up to Core.',
    he: 'עדיין אין הערות שנאספו אוטומטית. הן מופיעות לאחר שהמקבץ הלילי מקדם רשומות זיכרון בעדיפות גבוהה לליבה.',
  },
  agentObsLastPromotion: { en: 'Last promotion run: ', he: 'הרצת הקידום האחרונה: ' },
  agentObsSuggestionsHeader: { en: 'Suggestions to review ({n})', he: 'הצעות לבדיקה ({n})' },
  agentObsSuggestionsHelp1: { en: 'Learned behavior awaiting review. ', he: 'התנהגות שנלמדה הממתינה לבדיקה. ' },
  agentObsSuggestionsHelp2: { en: ' promotes it into the agent’s rules; ', he: ' מקדם אותה לכללי הסוכן; ' },
  agentObsSuggestionsHelp3: { en: ' removes it.', he: ' מסירה אותה.' },
  agentObsApproveWord: { en: 'Approve', he: 'אישור' },
  agentObsDiscardWord: { en: 'Discard', he: 'דחייה' },
  agentObsNotesHeader: { en: 'Notes ({n})', he: 'הערות ({n})' },
  agentObsConflictsHeader: { en: 'Conflicts with your rules ({n})', he: 'התנגשויות עם הכללים שלך ({n})' },
  agentObsConflictsHelp: {
    en: 'The system wanted to promote these but they contradict your hand-authored rules. Review and either update your rules or discard.',
    he: 'המערכת רצתה לקדם אותן אך הן סותרות את הכללים שכתבת. בדוק ועדכן את הכללים שלך או דחה.',
  },
  agentObsCoreWants: { en: 'Core wants:', he: 'הליבה מבקשת:' },
  agentObsBrainHas: { en: 'Brain has ({field}):', he: 'המוח מכיל ({field}):' },
  agentObsConflictMeta: {
    en: 'source: {source} · status: {status} · {date}',
    he: 'מקור: {source} · סטטוס: {status} · {date}',
  },
  agentObsMemoryEmpty: {
    en: 'No Memory rows yet. Per-turn writes start populating this on the first AI conversation with this agent.',
    he: 'עדיין אין רשומות זיכרון. כתיבות לכל תור מתחילות לאכלס זאת בשיחת ה-AI הראשונה עם סוכן זה.',
  },
  agentObsMemoryIntro: {
    en: 'Showing the {n} most-recently-active customers. Anonymous rows (chat-widget customers without a login) are deleted after 30 days; identified users keep entries newer than 180 days.',
    he: 'מוצגים {n} הלקוחות הפעילים לאחרונה. רשומות אנונימיות (לקוחות רכיב צ׳אט ללא התחברות) נמחקות לאחר 30 יום; משתמשים מזוהים שומרים רשומות חדשות מ-180 יום.',
  },
  agentObsAnonymous: { en: '👤 Anonymous', he: '👤 אנונימי' },
  agentObsLastTurn: { en: 'last turn: ', he: 'תור אחרון: ' },
  agentObsAndMore: { en: '…and {n} more', he: '…ועוד {n}' },
  agentObsMessageRatings: { en: 'Message ratings', he: 'דירוגי הודעות' },
  agentObsMessageRatingsEmpty: {
    en: 'No message ratings yet. Customers rate individual bot replies via the chat widget; ratings appear here in real time.',
    he: 'עדיין אין דירוגי הודעות. לקוחות מדרגים תשובות בוט בודדות דרך רכיב הצ׳אט; הדירוגים מופיעים כאן בזמן אמת.',
  },
  agentObsMessageRatingsIntro: {
    en: 'Showing the {n} most recent ratings. 👍 {ups} · 👎 {downs}',
    he: 'מוצגים {n} הדירוגים האחרונים. 👍 {ups} · 👎 {downs}',
  },
  agentObsRefLabel: { en: 'ref', he: 'מזהה' },
  agentObsSatisfactionScore: { en: 'Satisfaction score', he: 'ציון שביעות רצון' },
  agentObsSatisfactionEmpty: {
    en: 'No satisfaction ratings yet. Customers submit a 1–5 score (and optional comment) via the feedback panel at the end of a conversation.',
    he: 'עדיין אין דירוגי שביעות רצון. לקוחות שולחים ציון 1–5 (והערה אופציונלית) דרך פאנל המשוב בסוף השיחה.',
  },
  agentObsSatisfactionIntro: {
    en: 'Showing the {n} most recent conversation ratings, newest first.',
    he: 'מוצגים {n} דירוגי השיחות האחרונים, החדשים ביותר תחילה.',
  },
  agentObsStarsAria: { en: '{n} out of 5 stars', he: '{n} מתוך 5 כוכבים' },
  agentObsCustomerLabel: { en: 'customer', he: 'לקוח' },
  agentObsApprove: { en: 'Approve', he: 'אשר' },
  agentObsApproving: { en: 'Approving…', he: 'מאשר…' },
  agentObsDiscard: { en: 'Discard', he: 'דחה' },
  agentObsDiscarding: { en: 'Discarding…', he: 'דוחה…' },
  agentObsApproveAria: { en: 'Approve {form} suggestion: {text}', he: 'אשר הצעת {form}: {text}' },
  agentObsDiscardAria: { en: 'Discard {form} suggestion: {text}', he: 'דחה הצעת {form}: {text}' },
  agentObsProposalActionFailed: { en: 'Failed to {action} proposal.', he: 'הפעולה {action} על ההצעה נכשלה.' },
  agentObsMgmtTitle: { en: 'Learned notes management', he: 'ניהול הערות שנלמדו' },
  agentObsMgmtDesc: {
    en: 'Maintenance tools for machine-curated learned notes. Use Re-sort to re-classify notes that may have been incorrectly sorted. Use Purge to remove stale notes by source or age.',
    he: 'כלי תחזוקה להערות שנלמדו ונאספו אוטומטית. השתמש במיון מחדש כדי לסווג מחדש הערות שאולי מוינו בטעות. השתמש בטיהור כדי להסיר הערות ישנות לפי מקור או גיל.',
  },
  agentObsResortTitle: { en: 'Re-sort learned notes', he: 'מיין מחדש הערות שנלמדו' },
  agentObsResortDesc: {
    en: 'Re-runs every learned note (not already a suggestion) through the classifier. Notes re-sorted as FACT go to the Learning Bank; BEHAVIOR notes become suggestions awaiting review. Existing suggestions are skipped.',
    he: 'מריץ מחדש כל הערה שנלמדה (שאינה כבר הצעה) דרך המסווג. הערות שממוינות מחדש כעובדה עוברות למאגר הלמידה; הערות התנהגות הופכות להצעות הממתינות לבדיקה. הצעות קיימות מדולגות.',
  },
  agentObsResorting: { en: 'Re-sorting…', he: 'ממיין מחדש…' },
  agentObsResortResult: {
    en: 'Processed {processed} · {removed} removed · {proposalsCreated} proposals · {factsWritten} facts · {skipped} skipped',
    he: 'עובדו {processed} · {removed} הוסרו · {proposalsCreated} הצעות · {factsWritten} עובדות · {skipped} דולגו',
  },
  agentObsResortLeft: { en: ' · {left} left', he: ' · {left} נותרו' },
  agentObsResortFailed: { en: 'Reclassify failed. Please try again.', he: 'הסיווג מחדש נכשל. נסה שוב.' },
  agentObsPurgeTitle: { en: 'Purge stale Core notes', he: 'טהר הערות ליבה ישנות' },
  agentObsPurgeDesc1: { en: 'Filter by source prefix (e.g. ', he: 'סנן לפי קידומת מקור (למשל ' },
  agentObsPurgeDesc2: {
    en: ') and/or an "added before" date. Run a ',
    he: ') ו/או תאריך "נוסף לפני". הפעל ',
  },
  agentObsPreviewWord: { en: 'Preview', he: 'תצוגה מקדימה' },
  agentObsPurgeDesc3: {
    en: ' first — Apply is locked until a preview has succeeded.',
    he: ' תחילה — ההחלה נעולה עד שתצוגה מקדימה מצליחה.',
  },
  agentObsSourcePrefixLabel: { en: 'Source prefix', he: 'קידומת מקור' },
  agentObsOptional: { en: '(optional)', he: '(אופציונלי)' },
  agentObsSourcePrefixPlaceholder: { en: 'e.g. post-turn:', he: 'למשל post-turn:' },
  agentObsSourcePrefixAria: { en: 'Source prefix filter for Core note purge', he: 'מסנן קידומת מקור לטיהור הערות ליבה' },
  agentObsAddedBeforeLabel: { en: 'Added before', he: 'נוסף לפני' },
  agentObsOptionalIsoDate: { en: '(optional, ISO date)', he: '(אופציונלי, תאריך ISO)' },
  agentObsAddedBeforeAria: { en: 'Added before date filter for Core note purge', he: 'מסנן תאריך "נוסף לפני" לטיהור הערות ליבה' },
  agentObsPreviewAria: { en: 'Preview Core note purge (dry run)', he: 'תצוגה מקדימה לטיהור הערות ליבה (הרצת ניסיון)' },
  agentObsPreviewing: { en: 'Previewing…', he: 'מציג תצוגה מקדימה…' },
  agentObsPreviewButton: { en: 'Preview (dry-run)', he: 'תצוגה מקדימה (הרצת ניסיון)' },
  agentObsApplyAria: { en: 'Apply Core note purge', he: 'החל טיהור הערות ליבה' },
  agentObsApplying: { en: 'Applying…', he: 'מחיל…' },
  agentObsApplyButton: { en: 'Apply purge', he: 'החל טיהור' },
  agentObsPreviewLabel: { en: 'Preview:', he: 'תצוגה מקדימה:' },
  agentObsPurgeMatched: { en: '{n} note{s} would be deleted.', he: '{n} הערות יימחקו.' },
  agentObsPurgeApplied: { en: 'Deleted {n} note{s}. {remaining} remaining.', he: 'נמחקו {n} הערות. {remaining} נותרו.' },
  agentObsPreviewFailed: { en: 'Preview failed. Please try again.', he: 'התצוגה המקדימה נכשלה. נסה שוב.' },
  agentObsPurgeFailed: { en: 'Purge failed. Please try again.', he: 'הטיהור נכשל. נסה שוב.' },
} satisfies Record<string, { en: string; he: string }>;
